import { collection, query, where, getDocs, doc, getDoc, addDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { UseHubName } from '../Hooks/UseHubName';
import GetUserData from '../Hooks/GetUserData';
import { sendExternalEmail } from '../Hooks/SendMailExternal';
import '../Styles/Config.css';

const Comunicaciones = () => {
  const { hubId } = useParams();
  const [usuarios, setUsuarios] = useState([]);
  const [destinatariosSeleccionados, setDestinatariosSeleccionados] = useState([]);
  const [extraDestinatarios, setExtraDestinatarios] = useState([]);
  const [asunto, setAsunto] = useState("");
  const [cuerpo, setCuerpo] = useState("");
  const [archivo, setArchivo] = useState([]);
  const [extraEmail, setExtraEmail] = useState("");
  const { userData } = GetUserData();

  const navigate = useNavigate();
  const hubName = UseHubName(hubId);

  // ============================================================================  
  // Cargar usuarios + destinatarios externos  
  // ============================================================================
  useEffect(() => {
    const cargarDatos = async () => {
      try {
        if (!hubId) return;

        // ---------- USUARIOS ----------
        const usuariosRef = collection(db, 'usuarios');
        const q = query(
          usuariosRef,
          where('hubId', 'array-contains-any', [hubId])
        );
        const querySnapshot = await getDocs(q);

        const usuariosList = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          externo: false,
        }));

        // ---------- SUPERUSERS ----------
        const hubDocRef = doc(db, 'private-hubs', hubId);
        const hubDocSnap = await getDoc(hubDocRef);
        let hubData = {};

        if (hubDocSnap.exists()) hubData = hubDocSnap.data();

        const superUsers = Array.isArray(hubData.superUsers) ? hubData.superUsers : [];

        const usuariosMarcados = usuariosList.map(u => ({
          ...u,
          isSuperUserHub: superUsers.includes(u.id),
        }));

        setUsuarios(usuariosMarcados);

        // ---------- DESTINATARIOS EXTERNOS ----------
        const destRef = collection(db, `private-hubs/${hubId}/Destinatarios`);
        const destSnap = await getDocs(destRef);

        const destinatariosList = destSnap.docs.map(d => ({
          id: d.id,
          email: d.data().email,
          externo: true,
        }));

        setExtraDestinatarios(destinatariosList);

        const textoInicialCuerpo = "\n\nPara acceder a la documentación, entre al hub a través del enlace proporcionado, haga login en la aplicación, entre en el Archivo Documental y encontrará la documentación dentro de la carpeta Comunicaciones, en una carpeta con el mismo nombre que el asunto de este correo \n\nEnlace de acceso al hub: https://tools-hub.lyseis.es";
        setCuerpo(textoInicialCuerpo);

      } catch (error) {
        console.error("Error al cargar datos del hub:", error);
      }
    };

    cargarDatos();
  }, [hubId]);

  // ============================================================================  
  // Añadir destinatario externo  
  // ============================================================================
  const handleAddExtraEmail = async () => {
    const email = extraEmail.trim().toLowerCase();

    if (!email) {
      alert("Introduce un email válido.");
      return;
    }

    try {
      const existeEnUsuarios = usuarios.some(
        u => u.email?.toLowerCase() === email
      );

      if (existeEnUsuarios) {
        alert("Este correo ya pertenece a un usuario del hub.");
        return;
      }

      const destRef = collection(db, `private-hubs/${hubId}/Destinatarios`);
      const destSnap = await getDocs(destRef);

      const existeEnDestinatarios = destSnap.docs.some(
        d => d.data().email?.toLowerCase() === email
      );

      if (existeEnDestinatarios) {
        alert("Este correo ya está añadido como destinatario externo.");
        return;
      }

      // Añadir destinatario
      await addDoc(destRef, {
        email,
        creado: new Date(),
      });

      alert("Destinatario añadido.");
      setExtraEmail("");

      const nuevosDestSnap = await getDocs(destRef);
      const destinatariosList = nuevosDestSnap.docs.map(d => ({
        id: d.id,
        email: d.data().email,
        externo: true,
      }));

      setExtraDestinatarios(destinatariosList);

    } catch (error) {
      console.error("Error añadiendo destinatario:", error);
      alert("No se pudo añadir el destinatario.");
    }
  };

  // ============================================================================  
  // Checkbox → añadir o quitar destinatarios  
  // ============================================================================
  const handleToggleDestinatario = (email) => {
    setDestinatariosSeleccionados(prev => {
      if (prev.includes(email)) {
        return prev.filter(e => e !== email);
      }
      return [...prev, email];
    });
  };

  const handleAutoSendExternal = async () => {

    const userEmail = userData?.email || "";
    const recipients = destinatariosSeleccionados;
    const subject = (asunto || "").trim();
    const text = (cuerpo || "").trim();
    const files = archivo ? Array.from(archivo) : [];
    if (!recipients.length) { alert("No hay destinatarios seleccionados."); return; }
    const r = await sendExternalEmail({ recipients, subject, text });
    if (r?.success) {
      const p = nowMadridParts();
      const tsDisplay = `${p.day}/${p.month}/${p.year} ${p.hour}:${p.minute}:${p.second}`;
      const tsFile = `${p.year}${p.month}${p.day}-${p.hour}${p.minute}${p.second}`;
      const html = buildReceiptHTML({ timestamp: tsDisplay, subject, text, recipients, files });
      //downloadHTML(`acuse-envio-${tsFile}.html`, html);
      files.push(new File([html], `acuse-envio-${tsFile}.html`, { type: 'text/html' }));
      try {
        const envioBT = await sendToBlocktrace(userEmail, recipients, files);
        console.log("Envío a Blocktrace:", envioBT);
        //guardarComunicacion(subject, recipients, envioBT);
        if(!envioBT[0].error){

          const envioHub = await sendInfoToHub(recipients, subject, envioBT, hubId, userEmail);
          console.log("Email del usuario:", userEmail);
          console.log("Envío a Hub:", envioHub);
        }
      } catch (error) {
        console.error("Error enviando a Blocktrace o al hub:", error);
      }
      alert("Emails enviados");
    } else {
      console.error("Error enviando email:", r);
      alert("Error al enviar");
    }
  }

  const buildReceiptHTML = ({timestamp, subject, text, recipients, files}) => {
    const recLis = (recipients||[]).map(r=>`<li>${esc(r)}</li>`).join('') || '<li>—</li>';
    const fileLis = (files||[]).map(f=>`<li>${esc(f.name)}</li>`).join('') || '<li>—</li>';
    const h = `<!DOCTYPE html><html lang="es"><head><meta charset="utf-8"><title>Acuse de envío</title><style>body{font-family:Arial,Helvetica,sans-serif;padding:20px;font-size:14px}h1{font-size:18px;margin:0 0 10px}h2{font-size:15px;margin:18px 0 8px}div.block{margin:10px 0}pre{white-space:pre-wrap;border:1px solid #ccc;padding:10px;border-radius:6px;background:#fafafa}ul{margin:6px 0 0 18px}</style></head><body><h1>Acuse de envío</h1><div class="block"><strong>Fecha y hora (Europa/Madrid):</strong> ${esc(timestamp)}</div><div class="block"><strong>Asunto:</strong> ${esc(subject||'')}</div><h2>Contenido</h2><pre>${esc(text||'')}</pre><h2>Destinatarios</h2><ul>${recLis}</ul><h2>Adjuntos</h2><ul>${fileLis}</ul></body></html>`;
    return h;
  }

  const nowMadridParts = () => {
    const p = new Intl.DateTimeFormat('es-ES',{timeZone:'Europe/Madrid',year:'numeric',month:'2-digit',day:'2-digit',hour:'2-digit',minute:'2-digit',second:'2-digit'}).formatToParts(new Date()).reduce((a,b)=>{a[b.type]=b.value;return a;}, {});
    return p;
  }

  function esc(s) {
    return String(s || '').replace(/[&<>"']/g, m => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[m]));
  }

  const sendToBlocktrace = async (userEmail, recipients, files) => {
    const cloudFunctionUrl = "https://blocktraceupload-aaetoibmfa-uc.a.run.app";
    const data = [];

    try{
      for (const file of files) {
        const cleanFileName = file.name
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "")
          .replace(/[^a-zA-Z0-9._-]/g, "_");

        const fileBase64 = await fileToBase64(file);

        const response = await fetch(cloudFunctionUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          mode: "cors",
          body: JSON.stringify({
            fileBase64,
            fileName: cleanFileName,
            userOrigin: userEmail,
            userDestination: recipients.join(", "),
            folderId: "default-folder", 
            processName: "UPLOAD_DOCUMENT",
          }),
        });

        const respuesta = await response.json();
        respuesta.fileName = cleanFileName;
        data.push(respuesta);
      }
    } catch(e) {
      console.error("Error enviando a Blocktrace:", e);
    }

    return data;
  }

  async function sendInfoToHub(recipients, subject, files, targetHub, correo) {
    const hubUrl = "https://us-central1-lyseis-tools-hub.cloudfunctions.net/emailDocUpload";
    try{

      recipients.push(correo);
      const response = await fetch(hubUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        mode: "cors",
        body: JSON.stringify({
          asuntoEmail: subject,
          documents: files,
          destinatarios: recipients.join(", "),
          targetHub: targetHub,
          comesFromHub: true,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error("Error al subir documentos:", errorData);
        return;
      }
    } catch(e) {
      console.error(e);
    }
  }

  // Convertir archivo a base64
  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result.split(",")[1]); // quitamos el prefijo "data:..."
      reader.onerror = (error) => reject(error);
    });
  };

  // ============================================================================  
  // Render  
  // ============================================================================
  return (
    <div className='lobby-container'>
      <div className="header-container">
        <header className="header">
          <h1 className="header-title">Módulo de comunicaciones de {hubName}</h1>
          <div className='logout-button-container'>
            <button onClick={() => navigate(-1)} className="logout-button">
              ← Volver
            </button>
          </div>
        </header>
      </div>

      <div className="data-box acciones-form" style={{maxWidth: '95%'}}>

        <div className="comunicaciones-grid">

          {/* IZQUIERDA – FORMULARIO */}
          <div className="comunicaciones-form">
            <h1 style={{ color: "#1E3A8A", marginBottom: '40px' }}>Enviar comunicación</h1>

            <label className="labelText">Asunto</label>
            <textarea
              value={asunto}
              onChange={(e) => setAsunto(e.target.value)}
              className="compras-form-input"
              style={{ height: "40px" }}
            />

            <label className="labelText">Cuerpo</label>
            <textarea
              value={cuerpo}
              onChange={(e) => setCuerpo(e.target.value)}
              className="compras-form-input"
              style={{ height: "150px" }}
            />

            <label className="labelText" style={{marginBottom: '10px'}}>Adjuntar archivo</label>
            <input 
              type="file" 
              multiple
              className="compras-form-input" 
              onChange={(e) => setArchivo(e.target.files)}
            />

            <button
              className="logout-button"
              style={{ marginTop: "20px" }}
              onClick={() =>{
                  console.log({
                    asunto,
                    cuerpo,
                    archivo,
                    destinatarios: destinatariosSeleccionados,
                  });
                  handleAutoSendExternal();
                }
              }
            >
              Enviar email
            </button>
          </div>

          {/* DERECHA – TABLA */}
          <div className="comunicaciones-tabla">
            <table className="data-table">
              <thead>
                <tr>
                  <th style={{ textAlign: "left" }}>Correo</th>
                  <th style={{ textAlign: "center" }}>Destinatario</th>
                </tr>
              </thead>

              <tbody>

                {/* USUARIOS INTERNOS */}
                {usuarios
                  .sort((a, b) => {
                    const aSuper = a.hubId?.includes("__superadmin__") ? 0 : 1;
                    const bSuper = b.hubId?.includes("__superadmin__") ? 0 : 1;
                    return aSuper - bSuper;
                  })
                  .map(user => (
                    <tr key={user.id}>
                      <td>{user.email}</td>
                      <td style={{ textAlign: "center" }}>
                        <input
                          type="checkbox"
                          checked={destinatariosSeleccionados.includes(user.email)}
                          onChange={() => handleToggleDestinatario(user.email)}
                        />
                      </td>
                    </tr>
                  ))}

                {/* DESTINATARIOS EXTERNOS */}
                {extraDestinatarios.map(dest => (
                  <tr key={dest.id} style={{ background: "#F5F7FF" }}>
                    <td>{dest.email}</td>
                    <td style={{ textAlign: "center" }}>
                      <input
                        type="checkbox"
                        checked={destinatariosSeleccionados.includes(dest.email)}
                        onChange={() => handleToggleDestinatario(dest.email)}
                      />
                    </td>
                  </tr>
                ))}

              </tbody>
            </table>

            {/* Añadir destinatario externo */}
            <div style={{ marginTop: "20px" }}>
              <label className="labelText">Añadir destinatario externo</label>
              <input
                type="email"
                value={extraEmail}
                onChange={(e) => setExtraEmail(e.target.value)}
                className="compras-form-input"
                placeholder="email@ejemplo.com"
                style={{ width: "100%" }}
              />

              <button
                className="logout-button"
                style={{ marginTop: "10px" }}
                onClick={handleAddExtraEmail}
              >
                Añadir destinatario
              </button>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default Comunicaciones;
